'use client';

import type { ImgHTMLAttributes, ReactNode } from 'react';
import { useImageVariant, type ImageVariantOptions } from '@/lib/image-variant';
import type { SupabaseImageTransformOptions } from '@/lib/utils';

type VariantImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'onError'> & {
  /** 原始地址（原图 / 视频首帧原图）；缩略图与回退由组件处理 */
  url: string;
  /** 展示规格：决定缩略图档位，启用 render 回退时也作为转换参数 */
  transform?: SupabaseImageTransformOptions;
  /** 已知缩略图状态时传入（素材库接口 thumbnailStatus === 'done'），省去 404 探测 */
  thumbnail?: ImageVariantOptions['thumbnail'];
  /** 原图也加载失败时渲染的内容（如类型图标）；默认不渲染 */
  fallback?: ReactNode;
};

/** 缩略图 → 原图 → fallback 的统一 <img>。需要 next/image 时直接使用 useImageVariant。 */
export function VariantImage({ url, transform, thumbnail, fallback = null, alt = '', ...imgProps }: VariantImageProps) {
  const { src, onError, exhausted } = useImageVariant(url, transform, { thumbnail });
  if (!src || exhausted) return <>{fallback}</>;
  // eslint-disable-next-line @next/next/no-img-element
  return <img {...imgProps} alt={alt} src={src} onError={onError} />;
}
